if (!window.Maximize.loadedScript.includes("product-card.js")) {
  window.Maximize.loadedScript.push("product-card.js");

  /**
   * Shared helper — syncs swatch label styles and input state from a parsed
   * response document back into the live card wrapper.
   * @param {Document} dataHTML
   * @param {Element}  rootEl
   */
  function syncSwatchOptionStyles(dataHTML, rootEl) {
    rootEl.querySelectorAll(".swatch-option__label").forEach((label) => {
      const sourceLabel = dataHTML.querySelector(
        `.swatch-option__label[data-position="${label.dataset.position}"]`
      );
      if (!sourceLabel) return;

      if (sourceLabel.hasAttribute("style")) {
        label.setAttribute("style", sourceLabel.getAttribute("style"));
      }

      const input = label.previousElementSibling;
      const newInput = sourceLabel.previousElementSibling;
      if (input && newInput) {
        input.classList.toggle("disabled", newInput.classList.contains("disabled"));
        input.classList.toggle("selected", newInput.classList.contains("selected"));
        input.checked = newInput.classList.contains("selected");
      }
    });
  }

  // ---------------------------------------------------------------------------
  // ProductCardSwatchOption
  // ---------------------------------------------------------------------------
  class ProductCardSwatchOption extends HTMLElement {
    static cacheData = {};

    constructor() {
      super();
      this.productCardWrapperEle = this.closest(".product-card__wrapper");
      this.productId = this.dataset.productId;
      this.sectionId = this.dataset.sectionId;
      this.optionId = this.dataset.optionId;
      this.productCardId = this.dataset.productCardId;
      this.productUrl = this.dataset.productUrl;
      this.effectImage = this.dataset.effectImage;
      this.isShowEDT = this.dataset.showEdt === "true";
      this.optionsSelectedValues = [];

      this.optionValueInputElements = Array.from(this.querySelectorAll(".variant-option__input"));
      this.optionLabelElements = Array.from(this.querySelectorAll(".swatch-option__label"));
      this.currentVariantEl = this.productCardWrapperEle.querySelector(".current-variant");
      this.currentVariant = this.currentVariantEl ? JSON.parse(this.currentVariantEl.textContent) : "";

      this.optionValueInputElements
        .filter((el) => el.classList.contains("selected"))
        .forEach((input) => {
          this.optionsSelectedValues[input.dataset.optionPosition - 1] = input.dataset.optionValueId;
        });
    }

    connectedCallback() {
      const handleClick = this.handleClickOptionLabel.bind(this);
      this.optionLabelElements.forEach((el) => {
        el.addEventListener("click", handleClick);
        el.addEventListener("keyup", (e) => {
          if (e.key === "Enter" || e.key === " ") handleClick(e);
        });
      });
    }

    handleClickOptionLabel(event) {
      event.preventDefault();
      const optionLabel = event.currentTarget;
      const option = optionLabel.previousElementSibling;
      const position = option.dataset.optionPosition;
      const idx = position - 1;

      this.optionValueInputElements
        .filter((el) => el.dataset.optionPosition === position)
        .forEach((el) => {
          const isTarget = el.dataset.optionValueId === option.dataset.optionValueId;
          el.checked = isTarget;
          el.classList.toggle("selected", isTarget);
          if (isTarget) this.optionsSelectedValues[idx] = el.dataset.optionValueId;
        });

      this.reRenderProductSwatches();
    }

    async reRenderProductSwatches() {
      const infoWrapper = this.closest(".product-card__info-wrapper");
      const buyButton = infoWrapper?.querySelector(".product-form__btn-add-to-cart");
      if (buyButton) buyButton.disabled = true;

      const selectedValues = this.optionsSelectedValues.filter(Boolean);
      this.productCardWrapperEle.dataset.listOptionValuesIdSelected = selectedValues.join(",");

      const url = `${this.productUrl}?option_values=${selectedValues.join(",")}&section_id=product-swatch`;
      let dataHTML = ProductCardSwatchOption.cacheData[url];

      if (!dataHTML) {
        try {
          const response = await fetch(url);
          const text = await response.text();
          dataHTML = new DOMParser().parseFromString(text, "text/html");
          ProductCardSwatchOption.cacheData[url] = dataHTML;
        } catch (error) {
          console.error(error);
          if (buyButton && !buyButton.ariaDisabled) buyButton.disabled = false;
          return;
        }
      }

      const root = this.productCardWrapperEle;

      // Update current variant info
      const newVariantInfoEl = dataHTML.querySelector(".current-variant");
      if (newVariantInfoEl) {
        this.currentVariant = JSON.parse(newVariantInfoEl.textContent);
        this.currentVariantEl.textContent = newVariantInfoEl.textContent;
      }

      // Update current variant pre-order info
      this.updateElements(root, dataHTML, '[type="application/json"][data-type="current-variant-additional-info"]');
      this.updateElements(root, dataHTML, '.product-card__properties-group');

      // Update current bundle item data
      this.updateElements(root, dataHTML, '[type="application/json"][data-type="current-bundle-item"]');

      if (this.isShowEDT) {
        this.updateElements(root, dataHTML, ".estimate-delivery-card-msg");
      }
      this.updateLabelData(root, dataHTML);
      if (this.currentVariant) syncSwatchOptionStyles(dataHTML, root);
      this.updateImage(dataHTML);
      this.dispatchVariantChangeEvent();

      if (buyButton && !buyButton.ariaDisabled) buyButton.disabled = false;
    }

    dispatchVariantChangeEvent() {
      const productCardId = this.productCardWrapperEle.id?.replace("ProductCard_", "") ?? "";
      document.dispatchEvent(
        new CustomEvent(`${PRODUCT_EVENT.swatchChange}${productCardId}`, {
          bubbles: true,
          detail: { productId: this.productId, currentVariant: this.currentVariant },
        })
      );
    }

    updateLabelData(rootEl, dataHTML) {
      const metafieldDataset = dataHTML.querySelector("#product-swatch")?.dataset.metafieldLabel;
      if (!metafieldDataset) return;

      const linkVariantEle = rootEl.querySelector(".link-product-variant");
      if (!linkVariantEle) return;

      const linkLabelsData = linkVariantEle.getAttribute("x-labels-data");
      if (!linkLabelsData) return;

      const metafieldDataObj = xParseJSON(metafieldDataset);
      const currentLabelData = xParseJSON(linkLabelsData);
      currentLabelData.metafield_label = metafieldDataObj.metafield_label;

      linkVariantEle.setAttribute("x-labels-data", JSON.stringify(currentLabelData));
      Alpine.store("xLabelsAndBadges")?.load(linkVariantEle);
    }

    updateElements(rootEl, dataHTML, selector) {
      const oldElements = rootEl.querySelectorAll(selector);
      const newElements = dataHTML.querySelectorAll(selector);
      const single = newElements.length === 1;

      oldElements.forEach((oldEl, i) => {
        const newEl = single ? newElements[0] : newElements[i];
        if (oldEl && newEl) oldEl.innerHTML = newEl.innerHTML;
      });
    }

    updateImage(dataHTML) {
      const root = this.productCardWrapperEle;
      const activeSlide = root.querySelector(".slide-item.is-active");
      const newImg = dataHTML.querySelector(".product-card__image");
      const newMediaId = newImg?.dataset.mediaId ?? null;

      if (this.effectImage === "carousel") {
        const target = root.querySelector(`.slide-item[data-media-id="${newMediaId}"]`);
        if (activeSlide && target) {
          activeSlide.classList.remove("is-active");
          target.classList.add("is-active");
          return;
        }
      }

      const img = activeSlide?.querySelector(".product-card__image");
      if (img && newImg) {
        img.setAttribute("srcset", newImg.getAttribute("srcset"));
        img.setAttribute("src", newImg.getAttribute("src"));
        if (activeSlide && this.effectImage === "carousel") {
          activeSlide.dataset.mediaId = newMediaId;
        }
      }
    }
  }

  if (!customElements.get("product-card-swatch-option")) {
    customElements.define("product-card-swatch-option", ProductCardSwatchOption);
  }

  class ChooseOptionElement extends HTMLElement {
    static cacheData = {};

    constructor() {
      super();
      this.optionInputElements = Array.from(this.querySelectorAll(".swatch-option__value"));
      this.optionLabelElements = Array.from(this.querySelectorAll(".swatch-option__label"));
      this.optionsSelectedValues = [];
      this.productId = this.dataset.productId;
      this.productUrl = this.dataset.productUrl;
      this.chooseOptionElementId = this.dataset.chooseOptionElementId;

      this.optionInputElements
        .filter((el) => el.classList.contains("selected"))
        .forEach((input) => {
          this.optionsSelectedValues[input.dataset.optionPosition - 1] = input.dataset.optionValueId;
        });

      this.currentVariantInfoEle = this.querySelector(
        '[type="application/json"][data-type="current-variant-info"]'
      );
      this.currentVariantAdditionalInfoEle = this.querySelector(
        '[type="application/json"][data-type="current-variant-additional-info"]'
      );
      if (this.currentVariantInfoEle) {
        this.currentVariant = JSON.parse(this.currentVariantInfoEle.textContent);
      }
    }

    connectedCallback() {
      const handleClick = this.handleClickOptionLabel.bind(this);
      this.optionLabelElements.forEach((el) => {
        el.addEventListener("click", handleClick);
        el.addEventListener("keyup", (e) => {
          if (e.key === "Enter" || e.key === " ") handleClick(e);
        });
      });
    }

    handleClickOptionLabel(event) {
      event.preventDefault();
      const optionLabel = event.currentTarget;
      const option = optionLabel.previousElementSibling;
      const position = option.dataset.optionPosition;
      const idx = position - 1;

      const selectedValue = this.querySelector(`.seclected-value-${position}`);
      if (selectedValue) selectedValue.textContent = option.dataset.value;

      this.optionInputElements
        .filter((el) => el.dataset.optionPosition === position)
        .forEach((el) => {
          const isTarget = el.dataset.optionValueId === option.dataset.optionValueId;
          el.checked = isTarget;
          el.classList.toggle("selected", isTarget);
          if (isTarget) this.optionsSelectedValues[idx] = el.dataset.optionValueId;
        });

      this.reRenderChooseOption();
    }

    async reRenderChooseOption() {
      const variantPickerEle = this.closest(".variant-picker__wrapper");
      const indexParam = variantPickerEle?.dataset?.indexParam;
      const chooseOptionWrapper = this.closest(".choose-option");
      const buyButton = chooseOptionWrapper?.querySelector(".product-form__btn-add-to-cart");
      if (buyButton) buyButton.disabled = true;

      const url = `${this.productUrl}?option_values=${this.optionsSelectedValues.join(",")}&section_id=choose-option&page=${indexParam}`;
      let dataHTML = ChooseOptionElement.cacheData[url];

      if (!dataHTML) {
        try {
          const response = await fetch(url);
          const text = await response.text();
          dataHTML = new DOMParser().parseFromString(text, "text/html");
          ChooseOptionElement.cacheData[url] = dataHTML;
        } catch (error) {
          console.error(error);
          if (buyButton && !buyButton.ariaDisabled) buyButton.disabled = false;
          return;
        }
      }

      this.updateElements(chooseOptionWrapper, dataHTML, ".choose-option__price-wrapper");
      this.updateElements(chooseOptionWrapper, dataHTML, ".choose-options-content .choose-option__properties-group");

      const newVariantInfoEl = dataHTML.querySelector(
        '[type="application/json"][data-type="current-variant-info"]'
      );
      if (newVariantInfoEl) {
        this.currentVariant = JSON.parse(newVariantInfoEl.textContent);
        this.currentVariantInfoEle.textContent = newVariantInfoEl.textContent;
      }

      const newAdditionalInfoEl = dataHTML.querySelector(
        '[type="application/json"][data-type="current-variant-additional-info"]'
      );
      if (this.currentVariantAdditionalInfoEle && newAdditionalInfoEl) {
        this.currentVariantAdditionalInfoEle.textContent = newAdditionalInfoEl.textContent;
      }

      syncSwatchOptionStyles(dataHTML, chooseOptionWrapper);
      // Suppress Safari's ::before recreation transition BEFORE re-enabling or dispatching event.
      // Safari recreates ::before when a button transitions from disabled/aria-disabled → enabled,
      // and plays an unwanted shrink animation (transform: none → scale(0)).
      //
      // TWO cases covered:
      // (a) available → available: button.disabled toggled below (non-aria-disabled)
      // (b) unavailable → available: button has aria-disabled="true" (set by Alpine),
      //     SKIPPED in the if-check below, but Alpine removes aria-disabled in a microtask
      //     triggered by dispatchChangeVariantEvent(). Class must be present at that moment.
      //
      // Timeline:
      //   [sync]      add is-variant-changing to button
      //   [sync]      re-enable if not aria-disabled (case a)
      //   [sync]      dispatchChangeVariantEvent → Alpine microtask queued
      //   [microtask] Alpine removes aria-disabled → ::before created → transition: none ✅ (case b)
      //   [rAF 1]     first frame
      //   [rAF 2]     remove is-variant-changing → transitions restored
      if (buyButton) {
        buyButton.classList.add('is-variant-changing');

        if (!buyButton.ariaDisabled) {
          buyButton.disabled = false;
        }

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            buyButton.classList.remove('is-variant-changing');
          });
        });
      }

      syncSwatchOptionStyles(dataHTML, chooseOptionWrapper);
      if (buyButton && !buyButton.ariaDisabled) buyButton.disabled = false;
      this.dispatchChangeVariantEvent();
    }

    updateElements(rootEl, dataHTML, selector) {
      const oldEl = rootEl.querySelector(selector);
      const newEl = dataHTML.querySelector(selector);
      if (oldEl && newEl) oldEl.innerHTML = newEl.innerHTML;
    }

    dispatchChangeVariantEvent() {
      document.dispatchEvent(
        new CustomEvent(`${PRODUCT_EVENT.swatchChange}${this.chooseOptionElementId}`, {
          bubbles: true,
          detail: { productId: this.productId, currentVariant: this.currentVariant },
        })
      );
    }
  }

  if (!customElements.get("choose-option")) {
    customElements.define("choose-option", ChooseOptionElement);
  }

  // ---------------------------------------------------------------------------
  // Product-card image scroller (swiffy carousel) boot
  //
  // The 1.9 perf rewrite lazy-inits Alpine on below-fold sections (x-ignore,
  // theme.js setupAlpineLazyInit). Re-initing those sections on scroll does NOT
  // re-fire the card's `x-intersect.once` that boots the swiffy carousel, so on
  // collection grids / promotional / recently-viewed cards the image scroller
  // never started. Mirroring theme.js's setupHighlightObserver, a standalone
  // IntersectionObserver boots each card independently of Alpine's DOM walk.
  // Storefront-only: in the theme editor the liquid keeps the x-intersect
  // attribute (design_mode), so this stays off there to avoid double-init.
  // ---------------------------------------------------------------------------
  (function bootProductCardSwiffy() {
    if (typeof Shopify !== "undefined" && Shopify.designMode) return;

    // `data-card-swiffy` is our own boot marker — stamped on the standard
    // product-card image wrapper and on the big-collection-template card.
    const SELECTOR = "[data-card-swiffy]";
    const SWIFFY_CONFIG = { effect: "fade", isLoop: false, paginationBar: "number_bar" };

    // Boot a single card's swiffy carousel via the Alpine stores.
    function boot(el) {
      if (el.dataset.swiffySliderInitialized === "true") return;
      Alpine.store("xSwiffy").load(el, SWIFFY_CONFIG);
      Alpine.store("xProductCard").handleCardHover(el);
    }

    function bootAll(root) {
      (root || document).querySelectorAll(SELECTOR).forEach(boot);
    }

    function bootReady() {
      return (
        typeof Alpine !== "undefined" &&
        Alpine.store &&
        Alpine.store("xSwiffy") &&
        Alpine.store("xProductCard") &&
        typeof SwiffySlider !== "undefined"
      );
    }

    // Alpine (and the SwiffySlider vendor) are interaction-deferred in this theme,
    // and this script's load order relative to them varies per page — so the
    // `alpine:initialized` event may fire before this runs (missed) or after.
    // Poll for readiness instead: it cannot miss regardless of ordering.
    let readyWaiting = false;
    function whenReady(cb) {
      if (bootReady()) {
        cb();
        return;
      }
      if (readyWaiting) return;
      readyWaiting = true;
      const iv = setInterval(() => {
        if (!bootReady()) return;
        clearInterval(iv);
        readyWaiting = false;
        cb();
      }, 200);
    }

    // Boot every card once Alpine is ready. Deliberately NOT viewport-gated:
    // product cards live in horizontal carousels (home, featured collections)
    // where IntersectionObserver reports the off-screen slides as non-intersecting
    // and never re-fires, so lazy gating silently skips them. boot() only runs
    // after user interaction (Alpine is deferred) so it is off the critical path,
    // and it is idempotent (swiffySliderInitialized guard).
    whenReady(() => bootAll());

    // Catch cards injected after load (facet filtering, pagination, quick view,
    // recently-viewed). Batch scans in a microtask to avoid thrashing.
    let scanQueued = false;
    const scheduleScan = () => {
      if (scanQueued) return;
      scanQueued = true;
      Promise.resolve().then(() => {
        scanQueued = false;
        whenReady(() => bootAll());
      });
    };
    new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches?.(SELECTOR) || node.querySelector?.(SELECTOR)) {
            scheduleScan();
            return;
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  })();
}
